/* Help centre content.
 *
 * Written against what the app actually does, screen by screen, so the
 * answers stay true as behaviour changes - if you change a screen, update
 * its entry here. `keywords` exist purely to widen the search: a user asks
 * "why can't I see my clients", not "Client Master visibility".
 */

export const GUIDES = [
  {
    id: 'dashboard',
    panel: 'Dashboard',
    summary: 'Your compliance position for the selected company and financial year.',
    keywords: ['dashboard', 'penalty exposure', 'compliance score', 'overdue', 'action required', 'home'],
    body: [
      'Penalty Exposure is what you would owe if every overdue filing were still unfiled today, plus penalties already crystallised on returns you filed late. It is not a bill — it is an estimate based on the per-day penalty of each form.',
      'Compliance Score is the share of filings that were due so far this year and were filed on or before their due date.',
      'Action Required lists everything overdue plus anything due in the next 7 days. Filing an item from here updates the dashboard immediately.',
      'Filing Status Overview shows filed vs total per category, including any custom categories such as FEMA or RBI that you have subscribed to.',
    ],
  },
  {
    id: 'calendar',
    panel: 'Compliance Calendar',
    summary: 'Every statutory due date for the company, month by month.',
    keywords: ['calendar', 'due date', 'file now', 'prepare', 'month', 'deadline'],
    body: [
      'Use the arrows to move between months. The financial year in the header follows along automatically when you cross April.',
      'Each item shows its due date, form and category. Overdue items are red, items due within 7 days amber.',
      'File Now records the filing date (and deposit date where the form needs one). Edit lets you correct a date afterwards, or clear it entirely with "Clear (Mark Unfiled)".',
      'Items marked Not Applicable still appear, greyed and labelled N/A, so nothing silently disappears — they are simply excluded from dashboard numbers and penalties.',
    ],
  },
  {
    id: 'gst',
    panel: 'GST Returns',
    summary: 'GSTR-1 / GSTR-3B tracking, monthly or quarterly (QRMP), plus YTD figures.',
    keywords: ['gst', 'gstr-1', 'gstr-3b', 'qrmp', 'iff', 'quarterly', 'itc', 'liability'],
    body: [
      'Whether you see monthly or quarterly returns depends on GST Filing Frequency on the company (Settings → Company Profile). Quarterly (QRMP) filers get GSTR-1 (Quarterly), GSTR-3B (Quarterly) and optional monthly IFF instead of monthly returns.',
      'A quarterly return is keyed by the quarter it covers, represented by the quarter\'s last month — the Apr–Jun quarter is period 6 (June), not the July due month.',
      'GSTR-3B quarterly due dates follow the QRMP state groups: the 22nd for Group A states, the 24th for Group B (which includes Haryana, Delhi and UP). This is set from the company\'s registered State.',
      'The YTD tab shows Sales, GST Liability (output tax before ITC), ITC and Net GST per month.',
    ],
  },
  {
    id: 'tds',
    panel: 'TDS / TCS',
    summary: 'Section-wise deduction tracking, monthly deposits and quarterly returns.',
    keywords: ['tds', 'tcs', 'deposit', 'challan', '24q', '26q', '27q', 'section', 'bsr'],
    body: [
      'TDS Deposits are monthly and due by the 7th of the following month. Record the deposit date, challan number, BSR code and amount.',
      'TDS Return Filings are quarterly and split by return type: 24Q (salary) and 26Q (non-salary) are tracked as separate rows, each with its own filing date, acknowledgment number and penalty.',
      'If you do not deduct salary TDS, mark Form 24Q as Not Applicable under Settings → Compliance Applicability rather than leaving it overdue.',
      'Section-wise figures come from Data Entry → TDS Data Entry. The rate and nature are filled in automatically from the section you pick.',
    ],
  },
  {
    id: 'roc',
    panel: 'ROC / MCA',
    summary: 'Annual filings under the Companies Act / LLP Act.',
    keywords: ['roc', 'mca', 'aoc-4', 'mgt-7', 'form 8', 'form 11', 'dir-3', 'annual'],
    body: [
      'Which forms appear depends entirely on the company type: a Private Limited gets AOC-4, MGT-7, DIR-3 KYC, DPT-3 and ADT-1; an LLP gets Form 8 and Form 11.',
      'If this list looks wrong or empty, the company type is probably unset or incorrect. Fix it in Settings → Manage Companies → Edit, then use Rebuild Calendar on that company.',
      'The Filed On column shows the date you actually filed, which is separate from the due date — a late filing keeps its penalty even after it is marked filed.',
    ],
  },
  {
    id: 'data-entry',
    panel: 'Data Entry',
    summary: 'Where the numbers behind GST, TDS and Financials are entered.',
    keywords: ['data entry', 'gst data', 'tds data', 'revenue', 'financials', 'import', 'excel'],
    body: [
      'GST Data Entry captures monthly sales, output tax and ITC. Net payable is always recomputed by the server, never taken from what you type.',
      'TDS Data Entry captures section-wise gross and TDS amounts for a quarter. Rate and nature come from the section master automatically.',
      'Each tab has an Import button for bulk Excel upload, and "Import All (Excel)" handles every module in one workbook — one sheet per module.',
      'Templates come with dropdowns on every fixed-choice column, so values like Return Type or Quarter cannot be mistyped.',
    ],
  },
  {
    id: 'import',
    panel: 'Excel Import',
    summary: 'Bulk upload for every module, with duplicate handling and undo.',
    keywords: ['import', 'excel', 'upload', 'template', 'duplicate', 'bulk', 'undo', 'batch'],
    body: [
      'Always start from Download Template — the columns must match exactly, and the template carries dropdowns for fixed-choice fields.',
      'Choose how duplicates are handled: Skip (default), Update Existing, or — where a module genuinely allows repeated rows — Import Anyway as new.',
      'Rows that fail validation are skipped and listed with the reason; valid rows in the same file still import.',
      'Settings → Import History can undo a batch. For Client/DSC/ITR/Audit/Financials that deletes the imported rows; for GST/ROC/TDS it reverts them to unfiled. Anything edited by hand since the import is left alone and reported as skipped.',
    ],
  },
  {
    id: 'compliance-setup',
    panel: 'Compliance Setup',
    summary: 'Add regulatory compliances beyond the standard GST/TDS/ROC set.',
    keywords: ['compliance setup', 'fema', 'rbi', 'sebi', 'custom compliance', 'fla', 'subscribe'],
    body: [
      'Standard compliances (GST, TDS, ROC, Income Tax, PF, ESIC) are always active and need no setup.',
      'Optional categories — FEMA, RBI, SEBI, DGFT, Labour Law, Environment and others — are opt-in. Turn a category on, tick the specific filings that apply to you, and Add Selected.',
      'Subscribing generates real calendar items. Subscribing to FLA Return, for example, puts it on the calendar due 15 July.',
      'Event-based filings (FC-GPR, FC-TRS, ODI) have no fixed date, so subscribing records them without adding a dated item — add one when the triggering event happens.',
      'Add Custom Compliance creates a filing of your own. It is private to your company and never visible to other users of the platform.',
    ],
  },
  {
    id: 'applicability',
    panel: 'Compliance Applicability',
    summary: 'Turn off filings that genuinely do not apply to you.',
    keywords: ['applicability', 'not applicable', 'n/a', 'disable', 'hide form', 'pf', 'esic'],
    body: [
      'Category level switches off a whole area — no employees means PF and ESIC do not apply yet.',
      'Form level is finer: GSTR-9C only above the audit turnover threshold, or Form 24Q if you have no salaried staff.',
      'Periods lets you make a form applicable (or not) from a specific month or quarter onward — "24Q applies from Q2 FY 2026-27" — without editing every future year.',
      'Anything marked N/A stays visible on the calendar, greyed and labelled, and is excluded from dashboard counts and penalty exposure. It is reversible at any time.',
    ],
  },
  {
    id: 'alerts',
    panel: 'Alert Preferences',
    summary: 'Email reminders before due dates and a daily penalty digest.',
    keywords: ['alert', 'email', 'reminder', 'notification', 'digest', 'whatsapp', 'sms'],
    body: [
      'Set how many days before a due date you want reminding, and whether you want the daily penalty-exposure digest.',
      'Notification email defaults to the company email captured at signup. Additional emails (comma separated) are copied on every alert — useful for other management.',
      'Send Test Email confirms delivery immediately rather than waiting for the daily run.',
      'WhatsApp and SMS are not built yet. The mobile fields are stored for when they are.',
    ],
  },
  {
    id: 'users',
    panel: 'Users',
    summary: 'Add people to your company and control what they can see.',
    keywords: ['users', 'staff', 'team', 'roles', 'permission', 'access', 'admin', 'invite'],
    body: [
      'Admin can manage users and settings for your company. Consultant/Staff can work in the app but not manage users. Client is read-only.',
      'Users you create get access to your company only — never to any other company on the platform.',
      'Use Assignments to narrow someone to specific clients instead of the whole company.',
      'A user you create can log in straight away; there is no email verification step for admin-created accounts.',
    ],
  },
  {
    id: 'billing',
    panel: 'Plan & Billing',
    summary: 'Free trial, the Pro plan, and how to pay.',
    keywords: ['billing', 'plan', 'pro', 'free', 'trial', 'payment', 'upi', 'qr', 'price', 'subscription', 'upgrade'],
    body: [
      'Every new account starts on a 15-day free trial with full Pro access, no card required.',
      'Pro is ₹1,000 + ₹180 GST = ₹1,180 per month, or ₹14,160 for 12 months. Only the Tally Connector and API Keys need Pro.',
      'When the trial ends you drop to Free. All compliance tracking, GST/TDS/ROC, imports, alerts and users keep working, and nothing is deleted — only Tally and API access stop.',
      'To pay: pick the billing period, scan the QR (the amount is already filled in) or pay to the UPI ID shown, then send us the payment reference. Your reference code is shown on the screen and is already inside the QR.',
      'Access is activated by Krosifin once the payment is matched, usually the same working day.',
    ],
  },
  {
    id: 'tally',
    panel: 'Tally Connector & API Keys',
    summary: 'Sync figures from Tally instead of typing them (Pro plan).',
    keywords: ['tally', 'connector', 'api key', 'sync', 'ledger', 'mapping', 'integration'],
    body: [
      'Map your actual Tally ledger names to the fields Krosifin syncs — GST, TDS or Financials. Several ledgers can map to one field; the connector adds them up.',
      'Generate an API key for the connector. The key is shown once at creation, so copy it immediately — it cannot be retrieved later.',
      'Revoking a key blocks any connector using it straight away. Revoking always works, even on the Free plan.',
      'Both the connector and API keys require the Pro plan.',
    ],
  },
];

export const FAQS = [
  {
    id: 'faq-client-master',
    q: 'Why can\'t I see Client Master in the menu?',
    keywords: ['client master', 'missing', 'hidden', 'clients', 'menu', 'usage type'],
    a: 'Client Master only appears when the company\'s Usage Type is "Professional". Accounts created through signup start as "Business Owner", which assumes you track your own company rather than managing books for clients. Change it in Settings → Manage Companies → Edit → Usage Type. Nothing is deleted either way.',
  },
  {
    id: 'faq-no-roc',
    q: 'My ROC / MCA screen is empty, or shows the wrong forms',
    keywords: ['roc', 'empty', 'missing', 'aoc-4', 'mgt-7', 'form 11', 'company type', 'constitution'],
    a: 'ROC forms are chosen by company type, and accounts created through signup have no type set. Go to Settings → Manage Companies → Edit, set Company Type (Constitution), save, then click Rebuild Calendar for that company. Already-filed items are never touched by the rebuild.',
  },
  {
    id: 'faq-penalty-wrong',
    q: 'The penalty exposure looks wrong',
    keywords: ['penalty', 'wrong', 'exposure', 'late', 'incorrect', 'amount'],
    a: 'Penalty counts two things: overdue items still unfiled (growing daily), and returns you filed after their due date (fixed at the filing date). Check the Filed On date against the due date on the relevant screen — a return filed even one day late keeps its penalty permanently. If a form does not apply to you at all, mark it N/A under Compliance Applicability instead of leaving it overdue.',
  },
  {
    id: 'faq-import-fail',
    q: 'My Excel import failed with an error on some rows',
    keywords: ['import', 'failed', 'error', 'excel', 'rows', 'rejected', 'upload'],
    a: 'The error message names the row and the reason. The most common causes are a value that is not one of the allowed options (use the dropdowns in the template rather than typing), a date in an unexpected format, or referencing a period that has no matching compliance item. Valid rows in the same file still import — fix the listed rows and upload again.',
  },
  {
    id: 'faq-otp',
    q: 'I did not receive my verification code / password reset email',
    keywords: ['otp', 'email', 'code', 'verification', 'not received', 'password reset', 'signup'],
    a: 'Check spam first. Codes expire and can only be used once, and there is a 60-second wait between requests plus a limit per hour. Use "Resend code" rather than signing up again — signing up again will say the account already exists. If it still does not arrive, contact us on 9350880086.',
  },
  {
    id: 'faq-forgot-password',
    q: 'How do I reset my password?',
    keywords: ['password', 'forgot', 'reset', 'locked out', 'login'],
    a: 'On the sign-in screen click "Forgot password?", enter your email, and you will receive a 6-digit code. Enter the code with your new password (minimum 8 characters). The code can only be used once.',
  },
  {
    id: 'faq-trial-end',
    q: 'What happens when my free trial ends?',
    keywords: ['trial', 'end', 'expire', '15 days', 'free', 'lose data', 'downgrade'],
    a: 'You move to the Free plan. Nothing is deleted and no data is lost — compliance tracking, GST/TDS/ROC, imports, alerts and users all keep working. Only the Tally Connector and API Keys stop until you upgrade to Pro.',
  },
  {
    id: 'faq-how-to-pay',
    q: 'How do I pay for Pro, and how quickly is it activated?',
    keywords: ['pay', 'payment', 'upi', 'qr', 'activate', 'pro', 'upgrade', 'subscription'],
    a: 'Go to Settings → Plan & Billing, choose 1 month (₹1,180) or 12 months (₹14,160), and scan the QR — the amount and your reference code are already filled in. You can also pay to the UPI ID shown, quoting your reference code. Then call or email us and access is activated, usually the same working day.',
  },
  {
    id: 'faq-quarterly-gst',
    q: 'I am a QRMP filer but I see monthly GST returns',
    keywords: ['qrmp', 'quarterly', 'gst', 'monthly', 'gstr-1', 'gstr-3b', 'frequency'],
    a: 'Set GST Filing Frequency to "Quarterly (QRMP)" in Settings → Company Profile. Changing it only affects items generated afterwards; use Rebuild Calendar on that company to apply it to the current year. Already-filed returns are preserved.',
  },
  {
    id: 'faq-tds-sections',
    q: 'Which TDS section codes should I use — the old ones or 392/393?',
    keywords: ['tds section', '392', '393', '194c', 'new act', 'old act', '2025', 'income tax act'],
    a: 'Both are listed. The Income-tax Act 2025 codes (392/393 series) apply from 01/04/2026 and are shown alongside their old-act equivalent, e.g. "194C / 393(1)-6(i).D(a)". Existing records keep whichever code they were saved with. Rates are maintained centrally by Krosifin — if one looks wrong, tell us.',
  },
  {
    id: 'faq-multi-company',
    q: 'Can I manage more than one company?',
    keywords: ['multiple companies', 'group', 'add company', 'switch', 'second company'],
    a: 'Yes. Use + Add Company in the company selector or Settings → Manage Companies, then switch between them from the selector in the header. Consolidated View shows every company side by side. Additional companies inherit your existing plan rather than starting a fresh trial.',
  },
  {
    id: 'faq-na-vs-delete',
    q: 'Should I delete a filing that does not apply to me?',
    keywords: ['delete', 'remove', 'not applicable', 'n/a', 'unwanted', 'form'],
    a: 'Mark it Not Applicable under Settings → Compliance Applicability instead. It stays visible on the calendar, greyed and labelled, and stops counting toward penalties and your compliance score — but the history remains, and you can reverse it at any time.',
  },
];

/** Very small keyword-relevance search over the curated content above.
 *  This is deliberately not an AI model: answers are only ever text that
 *  was written here, so it can never invent a due date or a rate. */
export function searchHelp(query) {
  const q = (query || '').toLowerCase().trim();
  if (!q) return [];
  const terms = q.split(/\s+/).filter((t) => t.length > 2);
  if (!terms.length) return [];

  const score = (haystack, weight) => {
    const text = haystack.toLowerCase();
    return terms.reduce((n, t) => (text.includes(t) ? n + weight : n), 0);
  };

  const results = [
    ...FAQS.map((f) => ({
      kind: 'faq',
      id: f.id,
      title: f.q,
      text: f.a,
      score: score(f.q, 3) + score(f.keywords.join(' '), 2) + score(f.a, 1),
    })),
    ...GUIDES.map((g) => ({
      kind: 'guide',
      id: g.id,
      title: g.panel,
      text: g.body.join(' '),
      score: score(g.panel, 3) + score(g.keywords.join(' '), 2) + score(g.summary, 1) + score(g.body.join(' '), 1),
    })),
  ];

  return results.filter((r) => r.score > 0).sort((a, b) => b.score - a.score).slice(0, 5);
}
